/* eslint-disable prettier/prettier */
import { FunctionComponent, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Sidebar } from "primereact/sidebar";
import * as XLSX from "xlsx";
import WarehouseBankService from "services/warehouse-bank-service";
import { setToaster } from "common/toastStore";

interface WarehouseBankRow {
  id: number;
  name: string;
  interiumFundingID: string;
  gestationId: string;
}

interface WarehouseBankForm {
  id: number | null;
  name: string;
  interiumFundingID: string;
  gestationId: string;
}

const DEFAULT_FORM: WarehouseBankForm = {
  id: null,
  name: "",
  interiumFundingID: "",
  gestationId: "",
};

const normalizeText = (value: string) => value.trim().toLowerCase();
const keepDigits = (value: string) => value.replace(/\D/g, "");

const mapRow = (row: any): WarehouseBankRow => ({
  id: Number(row?.id ?? row?.Id ?? 0),
  name: row?.name ?? row?.Name ?? "",
  interiumFundingID: row?.interiumFundingID ?? row?.InteriumFundingID ?? "",
  gestationId: row?.gestationId ?? row?.GestationId ?? "",
});

const WarehouseBank: FunctionComponent = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [rows, setRows] = useState<WarehouseBankRow[]>([]);
  const [addVisible, setAddVisible] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [form, setForm] = useState<WarehouseBankForm>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadWarehouseBanks = () => {
    setLoading(true);
    WarehouseBankService.getWarehouseBanks()
      .then((response: any) => {
        const data = Array.isArray(response?.data) ? response.data : [];
        setRows(data.map(mapRow));
      })
      .catch((error: Error) => {
        console.log(error);
        setToaster({
          severity: "error",
          summary: "Load failed",
          detail: "Unable to load warehouse bank records.",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadWarehouseBanks();
  }, []);

  const openAddDrawer = () => {
    setIsEditMode(false);
    setForm(DEFAULT_FORM);
    setErrors({});
    setAddVisible(true);
  };

  const openEditDrawer = (rowData: WarehouseBankRow) => {
    setIsEditMode(true);
    setForm({
      id: rowData.id,
      name: rowData.name,
      interiumFundingID: rowData.interiumFundingID,
      gestationId: rowData.gestationId,
    });
    setErrors({});
    setAddVisible(true);
  };

  const closeDrawer = () => {
    setAddVisible(false);
    setIsEditMode(false);
    setForm(DEFAULT_FORM);
    setErrors({});
  };

  const hasDuplicateName = () => {
    const normalizedName = normalizeText(form.name);
    if (!normalizedName) return false;

    return rows.some((row) => {
      if (row.id === form.id) return false;
      return normalizeText(row.name) === normalizedName;
    });
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!form.name.trim()) nextErrors.name = "Name is required.";
    if (!form.interiumFundingID.trim()) {
      nextErrors.interiumFundingID = "Interium Funding ID is required.";
    } else if (!/^\d+$/.test(form.interiumFundingID.trim())) {
      nextErrors.interiumFundingID = "Interium Funding ID must be numeric.";
    }
    if (!form.gestationId.trim()) {
      nextErrors.gestationId = "Gestation Id is required.";
    } else if (!/^\d+$/.test(form.gestationId.trim())) {
      nextErrors.gestationId = "Gestation Id must be numeric.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (hasDuplicateName()) {
      setToaster({
        severity: "warn",
        summary: "Duplicate name",
        detail: "A warehouse bank with the same Name already exists.",
      });
      return;
    }

    const payload = {
      Id: form.id || 0,
      Name: form.name.trim(),
      InteriumFundingID: form.interiumFundingID.trim(),
      GestationId: form.gestationId.trim(),
    };

    setSaving(true);

    try {
      if (isEditMode) {
        await WarehouseBankService.updateWarehouseBank(payload);
      } else {
        await WarehouseBankService.createWarehouseBank(payload);
      }

      setToaster({
        severity: "success",
        summary: isEditMode ? "Updated" : "Created",
        detail: `Warehouse bank record ${isEditMode ? "updated" : "created"} successfully.`,
      });
      closeDrawer();
      loadWarehouseBanks();
    } catch (error: any) {
      console.log(error);
      setToaster({
        severity: "error",
        summary: isEditMode ? "Update failed" : "Create failed",
        detail:
          error?.response?.data ||
          `Unable to ${isEditMode ? "update" : "create"} warehouse bank record.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const renderActions = (rowData: WarehouseBankRow) => {
    return (
      <Button
        label="Edit"
        icon="pi pi-pencil"
        className="p-button-outlined p-button-sm"
        onClick={() => openEditDrawer(rowData)}
      />
    );
  };

  const exportToExcel = () => {
    if (!rows.length) {
      setToaster({
        severity: "info",
        summary: "No data",
        detail: "No records to export.",
      });
      return;
    }

    const exportRows = rows.map((row) => ({
      Name: row.name,
      "Interium Funding ID": row.interiumFundingID,
      "Gestation Id": row.gestationId,
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "WarehouseBank");
    XLSX.writeFile(wb, `warehouse_bank_export_${new Date().toISOString()}.xlsx`);
  };

  const renderHeader = () => {
    return (
      <div className="flex align-items-center gap-3 w-full">
        <div>
          <h2 className="m-0 text-xl font-semibold">Warehouse Bank</h2>
          <p className="m-0 text-sm text-500">Manage warehouse bank entries.</p>
        </div>

        <div className="ml-auto flex gap-2">
          <Button
            label="Export to Excel"
            icon="pi pi-file-excel"
            className="p-button-outlined"
            onClick={exportToExcel}
          />
          <Button
            label="Add Warehouse Bank"
            icon="pi pi-plus"
            className="p-button-success"
            onClick={openAddDrawer}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <DataTable
        className="shadow-md"
        value={rows}
        loading={loading}
        stripedRows
        responsiveLayout="scroll"
        emptyMessage="No warehouse bank records found."
        header={renderHeader}
        dataKey="id"
      >
        <Column field="name" header="Name" />
        <Column field="interiumFundingID" header="Interium Funding ID" />
        <Column field="gestationId" header="Gestation Id" />
        <Column body={renderActions} header="Actions" style={{ width: "10rem" }} />
      </DataTable>

      <Sidebar
        visible={addVisible}
        position="right"
        style={{ width: "38rem" }}
        onHide={closeDrawer}
      >
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="m-0 text-lg font-semibold">
              {isEditMode ? "Edit Warehouse Bank" : "Add Warehouse Bank"}
            </h3>
            <p className="m-0 text-sm text-500">
              {isEditMode
                ? "Update the existing warehouse bank details."
                : "Create a new warehouse bank entry."}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="warehouse-bank-name">Name</label>
            <InputText
              id="warehouse-bank-name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className={errors.name ? "p-invalid" : ""}
            />
            {errors.name && <small className="p-error">{errors.name}</small>}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="warehouse-bank-funding-id">Interium Funding ID</label>
            <InputText
              id="warehouse-bank-funding-id"
              value={form.interiumFundingID}
              inputMode="numeric"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  interiumFundingID: keepDigits(e.target.value),
                }))
              }
              className={errors.interiumFundingID ? "p-invalid" : ""}
            />
            {errors.interiumFundingID && (
              <small className="p-error">{errors.interiumFundingID}</small>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="warehouse-bank-gestation-id">Gestation Id</label>
            <InputText
              id="warehouse-bank-gestation-id"
              value={form.gestationId}
              inputMode="numeric"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  gestationId: keepDigits(e.target.value),
                }))
              }
              className={errors.gestationId ? "p-invalid" : ""}
            />
            {errors.gestationId && (
              <small className="p-error">{errors.gestationId}</small>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              label="Cancel"
              className="p-button-outlined"
              onClick={closeDrawer}
              disabled={saving}
            />
            <Button
              label={isEditMode ? "Update" : "Save"}
              icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"}
              onClick={handleSave}
              disabled={saving}
            />
          </div>
        </div>
      </Sidebar>
    </>
  );
};

export default WarehouseBank;